import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Typography, Spin, Empty, Tag, Table, Tooltip, notification } from 'antd';
import {
  InfoCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useEventSelection } from '../../../contexts/useEventSelection';
import { useAlertNotifications } from '../../../contexts/useAlertNotifications';
import { useCollectionFreshness } from '../../../hooks/useCollectionFreshness';
import alertsService, {
  type EventAlert,
  type AlertSeverity,
  type AlertType,
  ALERT_TYPE_CONFIG,
  SEVERITY_CONFIG,
} from '../../../api/alertsService';
import { showAlertToast } from '../../../components/AlertNotifications';
import type { WidgetProps } from './types';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;

const getSeverityIcon = (severity: AlertSeverity) => {
  switch (severity) {
    case 'info': return <InfoCircleOutlined style={{ color: SEVERITY_CONFIG.info.color }} />;
    case 'warning': return <WarningOutlined style={{ color: SEVERITY_CONFIG.warning.color }} />;
    case 'critical': return <ExclamationCircleOutlined style={{ color: SEVERITY_CONFIG.critical.color }} />;
    default: return null;
  }
};

const MAX_LATEST = 20;

export const AlertsWidget = ({ config, sizeClass }: WidgetProps) => {
  const { selectedEvent } = useEventSelection();
  const { compactAlerts } = useAlertNotifications();
  const [alerts, setAlerts] = useState<EventAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const isFirstLoadRef = useRef(true);

  const metadataTimestamps = useCollectionFreshness(['event_alerts'], {
    interval: 30_000,
    enabled: true,
  });
  const [alertRefreshCounter, setAlertRefreshCounter] = useState(0);
  const lastAlertFetchRef = useRef<string>('');
  useEffect(() => {
    const latest = metadataTimestamps.event_alerts;
    if (latest && latest > lastAlertFetchRef.current) {
      lastAlertFetchRef.current = latest;
      setAlertRefreshCounter(c => c + 1);
    }
  }, [metadataTimestamps]);

  const showActive = (config.showActive as boolean) ?? true;
  const showLatest = (config.showLatest as boolean) ?? false;

  const [notificationApi, contextHolder] = notification.useNotification();

  const tz = selectedEvent?.circuit?.time_zone || 'UTC';

  useEffect(() => {
    isFirstLoadRef.current = true;
    setAlerts([]);
    setLoading(false);
  }, [selectedEvent]);

  useEffect(() => {
    if (!selectedEvent) return;

    let cancelled = false;
    const isFirstLoad = isFirstLoadRef.current;

    if (isFirstLoad) {
      setLoading(true);
    }

    const fetchData = async () => {
      try {
        const isDemo = selectedEvent.demo ?? false;
        const res = await alertsService.getEventAlerts(
          selectedEvent.season_year,
          selectedEvent.circuit_id,
          selectedEvent.start_date,
          isDemo,
        );
        if (!cancelled && res.success && res.data) {
          setAlerts(res.data);
          if (isFirstLoad) {
            isFirstLoadRef.current = false;
            setLoading(false);
          }
        } else if (!cancelled && isFirstLoad) {
          isFirstLoadRef.current = false;
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          if (isFirstLoad) {
            setAlerts([]);
            setLoading(false);
          } else {
            console.warn('Alerts widget silent refresh failed');
          }
        }
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [selectedEvent, alertRefreshCounter]);

  const sevOrder: Record<AlertSeverity, number> = { critical: 0, warning: 1, info: 2 };

  const displayAlerts = useMemo(() => {
    const now = new Date();
    const nonDeleted = alerts.filter((a) => !a.is_deleted);

    const active = showActive
      ? nonDeleted.filter((a) => new Date(a.end_time) > now)
      : [];

    const latest = showLatest
      ? [...nonDeleted]
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, MAX_LATEST)
      : [];

    // Merge and deduplicate
    const seen = new Set<string>();
    const merged: EventAlert[] = [];
    for (const a of [...active, ...latest]) {
      if (!seen.has(a.id)) {
        seen.add(a.id);
        merged.push(a);
      }
    }

    return merged.sort((a, b) => (sevOrder[a.severity] ?? 3) - (sevOrder[b.severity] ?? 3));
  }, [alerts, showActive, showLatest]);

  const handleAlertClick = useCallback((alert: EventAlert) => {
    const notificationKey = `widget-preview-${alert.id}-${Date.now()}`;
    showAlertToast({
      api: notificationApi,
      alert,
      notificationKey,
      compact: compactAlerts,
      duration: 8,
      onAcknowledge: () => notificationApi.destroy(notificationKey),
    });
  }, [notificationApi, compactAlerts]);

  if (loading && alerts.length === 0) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="small" />
      </div>
    );
  }

  if (!selectedEvent) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize: 11 }}>No event selected</span>} />
      </div>
    );
  }

  if (selectedEvent.legacy) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize: 11 }}>Alerts unavailable for legacy events</span>} />
      </div>
    );
  }

  const emptyLabel = showActive && showLatest
    ? 'No alerts'
    : showActive
      ? 'No active alerts'
      : showLatest
        ? 'No recent alerts'
        : 'Enable active or latest alerts in settings';

  if (displayAlerts.length === 0) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize: 11 }}>{emptyLabel}</span>} />
      </div>
    );
  }

  const isCompact = sizeClass === 'compact';
  const now = new Date();

  const compactView = (
    <div style={{ height: '100%', overflow: 'auto', padding: 6 }}>
      {contextHolder}
      {displayAlerts.map((alert) => {
        const expired = new Date(alert.end_time) <= now;
        return (
          <div
            key={alert.id}
            onClick={() => handleAlertClick(alert)}
            style={{
              padding: '4px 6px',
              marginBottom: 4,
              borderRadius: 4,
              borderLeft: `3px solid ${SEVERITY_CONFIG[alert.severity]?.color || '#999'}`,
              background: 'rgba(128, 128, 128, 0.04)',
              cursor: 'pointer',
              opacity: expired ? 0.55 : 1,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {getSeverityIcon(alert.severity)}
              <Text strong style={{ fontSize: 11, flex: 1 }} ellipsis>
                {alert.title}
              </Text>
              {expired && <Tag style={{ fontSize: 9, margin: 0, lineHeight: '14px', padding: '0 3px' }}>Ended</Tag>}
            </div>
            <Text type="secondary" style={{ fontSize: 10 }} ellipsis>
              {alert.message}
            </Text>
          </div>
        );
      })}
    </div>
  );

  const formatTime = (ts: string) => dayjs(ts).tz(tz).format('HH:mm');

  const columns: ColumnsType<EventAlert> = [
    {
      title: 'Sev.',
      dataIndex: 'severity',
      key: 'severity',
      width: 60,
      render: (severity: AlertSeverity) => (
        <Tag
          icon={getSeverityIcon(severity)}
          color={severity === 'critical' ? 'error' : severity === 'warning' ? 'warning' : 'processing'}
          style={{ fontSize: 10, margin: 0, padding: '0 4px', lineHeight: '18px' }}
        >
          {SEVERITY_CONFIG[severity]?.label}
        </Tag>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'alert_type',
      key: 'alert_type',
      width: 90,
      render: (alertType: AlertType) => {
        const cfg = ALERT_TYPE_CONFIG[alertType];
        return (
          <Text style={{ fontSize: 11 }}>
            {cfg?.icon} {cfg?.label || alertType}
          </Text>
        );
      },
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title: string) => <Text strong style={{ fontSize: 11 }}>{title}</Text>,
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (msg: string) => (
        <Tooltip title={msg}>
          <Text style={{ fontSize: 11 }}>{msg}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Start',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 60,
      render: (ts: string) => <Text type="secondary" style={{ fontSize: 10 }}>{formatTime(ts)}</Text>,
    },
    {
      title: 'End',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 60,
      render: (ts: string) => <Text type="secondary" style={{ fontSize: 10 }}>{ts ? formatTime(ts) : '—'}</Text>,
    },
  ];

  if (showLatest) {
    columns.push({
      title: 'Status',
      key: 'status',
      width: 60,
      render: (_, record) => {
        const expired = new Date(record.end_time) <= now;
        return expired
          ? <Tag style={{ fontSize: 9, margin: 0, lineHeight: '16px', padding: '0 3px' }}>Ended</Tag>
          : <Tag color="green" style={{ fontSize: 9, margin: 0, lineHeight: '16px', padding: '0 3px' }}>Active</Tag>;
      },
    });
  }

  if (isCompact) return compactView;

  return (
    <div style={{ height: '100%', overflow: 'hidden', padding: '4px 0' }}>
      {contextHolder}
      <Table
        dataSource={displayAlerts}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={false}
        scroll={{ y: 'calc(100% - 28px)' }}
        style={{ fontSize: 11 }}
        onRow={(record) => ({
          onClick: () => handleAlertClick(record),
          style: {
            cursor: 'pointer',
            opacity: new Date(record.end_time) <= now ? 0.55 : 1,
          },
        })}
      />
    </div>
  );
};
